import React from "react";
import { Mail, Phone, Globe, MapPin, Clock } from "lucide-react";
import { Container } from "@/components/ui-kit";
import ContactForm from "@/components/ContactForm";
import { SITE, REGIONS } from "@/data/content";
import { legalBusinessIdentity } from "@/data/legalBusinessIdentity";
import { useRegion } from "@/hooks/useRegion";

export default function Contact() {
  const { region, isPH } = useRegion();
  const site = REGIONS[region];
  return (
    <div data-testid="contact-page">
      <section className="bg-[#0F172A]">
        <Container className="py-12 md:py-16 text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-3">
            {isPH ? "Contact Us — Philippines" : "Contact Us"}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-white text-balance">
            Let's talk about your website
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
            {isPH
              ? "Reach out and we'll help you figure out the right level of website responsibility for your Philippine business. No pressure. No sales scripts. Just clarity."
              : "If you want to understand whether this makes sense for your business, reach out. No pressure. No sales scripts. Just clarity."}
          </p>
        </Container>
      </section>

      <section className="py-20 md:py-24 bg-[#F8FAFC]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Info */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm mb-8">
                <img src="/images/contact.png" alt="Contact MySiteCare" className="w-full h-52 object-cover" />
              </div>
              <h2 className="text-2xl font-bold font-display text-[#0F172A]">Contact details</h2>
              <p className="mt-2 text-[#475569]">We'll confirm what you have, what you need, and which option fits.</p>

              <div className="mt-8 space-y-5">
                <a href={`mailto:${SITE.email}`} data-testid="contact-info-email" className="flex items-start gap-4 group">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><Mail size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Email</span>
                    <span className="text-[#0F172A] font-medium group-hover:text-[#10B981] transition-colors">{SITE.email}</span>
                  </span>
                </a>
                <a href={`tel:${SITE.phoneHref}`} data-testid="contact-info-phone" className="flex items-start gap-4 group">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><Phone size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Phone</span>
                    <span className="text-[#0F172A] font-medium group-hover:text-[#10B981] transition-colors">{SITE.phone}</span>
                  </span>
                </a>
                <a href={site.website} target="_blank" rel="noopener noreferrer" data-testid="contact-info-website" className="flex items-start gap-4 group">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><Globe size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Website</span>
                    <span className="text-[#0F172A] font-medium group-hover:text-[#10B981] transition-colors">{site.website.replace("https://", "")}</span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><MapPin size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Service area</span>
                    <span data-testid="contact-service-area" className="text-[#0F172A] font-medium">{isPH ? "Philippines" : SITE.serviceArea}</span>
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><MapPin size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Registered / Business Address</span>
                    <span data-testid="contact-registered-business-address" className="text-[#0F172A] font-medium">{legalBusinessIdentity.registeredBusinessAddress}</span>
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="rounded-xl bg-[#10B981]/10 p-3"><Clock size={20} className="text-[#10B981]" /></span>
                  <span>
                    <span className="block text-sm text-[#94A3B8]">Response</span>
                    <span className="text-[#0F172A] font-medium">We reply within a reasonable timeframe</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-sm p-8 md:p-10">
                <h2 className="text-2xl font-bold font-display text-[#0F172A]">Send us a message</h2>
                <p className="mt-2 text-[#475569] mb-8">Fill in the form below and we'll get back to you.</p>
                <ContactForm />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
